import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import InterestClusterTileGrid from './InterestClusterTileGrid';

const PREVIEW_CLUSTER_IDS = [22, 33, 26, 8, 10, 29, 5, 16, 1, 4, 34, 21, 18, 14, 24];
/**
 * Frame sized for the tallest 5-tile preview. Fewer tiles leave more fade room
 * below; the CTA stays pinned so layout count changes never move the button.
 */
const PREVIEW_FRAME_CLASS =
  'relative h-[22rem] overflow-hidden rounded-2xl sm:h-[24rem]';

function shuffle(list) {
  const copy = [...list];
  for (let i = copy.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  return copy;
}

/** Pick 3, 4, or 5 for the next teaser layout. */
function nextPreviewTileCount() {
  return 3 + Math.floor(Math.random() * 3);
}

function buildPreviewClusters(tileCount) {
  const ids = shuffle(PREVIEW_CLUSTER_IDS).slice(0, tileCount);
  const scores = Array.from({ length: tileCount }, (_, index) => (
    0.26 - index * 0.035 + (Math.random() - 0.5) * 0.02
  ));

  return ids
    .map((clusterId, index) => ({
      cluster_id: clusterId,
      score: scores[index]
    }))
    .sort((a, b) => b.score - a.score);
}

/**
 * Teaser widget shown before the user has interest-test results — shuffles sample tiles.
 * @param {{ omitHeading?: boolean, showCta?: boolean }} props
 */
export default function InterestClusterPlaceholder({ omitHeading = false, showCta = true }) {
  const { t } = useTranslation();
  const [shuffleKey, setShuffleKey] = useState(0);
  const [visible, setVisible] = useState(true);
  const [tileCount, setTileCount] = useState(() => nextPreviewTileCount());
  const clusters = useMemo(
    () => buildPreviewClusters(tileCount),
    [shuffleKey, tileCount]
  );

  useEffect(() => {
    const interval = setInterval(() => {
      setVisible(false);
      window.setTimeout(() => {
        setTileCount(nextPreviewTileCount());
        setShuffleKey((key) => key + 1);
        setVisible(true);
      }, 320);
    }, 3200);
    return () => clearInterval(interval);
  }, []);

  return (
    <div>
      {!omitHeading ? (
        <div className="mb-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-violet-600">
            {t('interestTest.statsPreviewEyebrow')}
          </p>
          <h2 className="mt-1 text-xl font-bold text-ink">
            {t('report.interestClustersHeading')}
          </h2>
          <p className="mt-2 text-sm leading-6 text-ink-muted">
            {t('interestTest.statsPlaceholderDescription')}
          </p>
        </div>
      ) : null}

      <div className={PREVIEW_FRAME_CLASS}>
        <div
          className={`h-full transition-all duration-500 ease-out ${
            visible ? 'opacity-100 scale-100 blur-0' : 'opacity-40 scale-[0.98] blur-[1px]'
          }`}
          aria-hidden
        >
          <InterestClusterTileGrid
            clusters={clusters}
            showCaption={false}
            interactive={false}
            tileCount={tileCount}
          />
        </div>

        {/* Max bottom fade + blur so clipped tile content dissolves instead of shifting layout. */}
        <div
          className="pointer-events-none absolute inset-x-0 bottom-0 z-[1] h-[70%] backdrop-blur-[2px] [mask-image:linear-gradient(to_top,black_0%,black_40%,transparent_100%)] [-webkit-mask-image:linear-gradient(to_top,black_0%,black_40%,transparent_100%)]"
          aria-hidden
        />
        <div
          className="pointer-events-none absolute inset-x-0 bottom-0 z-[1] h-[68%] bg-gradient-to-t from-surface from-15% via-surface/85 via-40% to-transparent"
          aria-hidden
        />

        {showCta ? (
          <div className="absolute inset-x-0 bottom-0 z-[2] flex justify-center px-4 pb-5 pt-10">
            <Link
              to="/interest-test"
              className="inline-flex items-center justify-center rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground shadow-sm hover:bg-primary-hover focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-surface"
            >
              {t('interestTest.statsPlaceholderCta')}
            </Link>
          </div>
        ) : null}
      </div>
    </div>
  );
}
